import { useEffect, useRef, useState } from 'preact/hooks';
import { FieldHeader } from '../components/FieldHeader';
import { BASE_PATH } from '../../shared/types';
import { api, ApiError } from '../lib/api';
import { AppIcon } from '../components/AppIcon';
import { Brand } from '../components/Brand';

const RESEND_DELAY_MS = 60_000;

export function Login() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [resent, setResent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [linkError, setLinkError] = useState(() => new URLSearchParams(location.search).get('error') === 'invalid_link');
  const [resendAt, setResendAt] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const emailInput = useRef<HTMLInputElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (sent) heading.current?.focus();
    else emailInput.current?.focus();
  }, [sent]);

  useEffect(() => {
    if (!sent || !resendAt) return;
    const update = () => setRemaining(Math.max(0, Math.ceil((resendAt - Date.now()) / 1000)));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [sent, resendAt]);

  const requestLink = async () => {
    if (busy || (sent && Date.now() < resendAt)) return;
    setBusy(true); setError(null); setInvalidEmail(false);
    const address = email.trim().toLowerCase();
    try {
      await api.post('/auth/request-link', { email: address });
      setEmail(address);
      setResent(sent);
      setSent(true);
      setRemaining(60);
      setResendAt(Date.now() + RESEND_DELAY_MS);
      if (linkError) {
        setLinkError(false);
        history.replaceState(history.state, '', `${BASE_PATH}/login`);
      }
    } catch (err) {
      const invalid = err instanceof ApiError && err.code === 'invalid_email';
      setInvalidEmail(invalid);
      setError(invalid
        ? 'That email address does not look right. Check it and try again.'
        : 'We could not send the sign-in email right now. Please try again in a few minutes.');
    } finally {
      setBusy(false);
    }
  };

  const useDifferentEmail = () => {
    setSent(false); setResent(false); setError(null); setInvalidEmail(false);
    setResendAt(0); setRemaining(0);
  };

  return (
    <main class="auth-page narrow">
      <header class="auth-brand"><Brand /></header>
      <section class="auth-panel" aria-labelledby="login-title">
        {sent && <div class="auth-mail"><AppIcon name="mail" /></div>}
        <p class="eyebrow">{sent ? 'Your next step' : 'Registration, made simple.'}</p>
        <h1 id="login-title" class="auth-title" ref={heading} tabIndex={-1}>{sent ? 'Check your email.' : 'Let’s get you signed in.'}</h1>
        {sent ? (
          <>
            <p class="auth-intro">Look for a sign-in link at<br /><strong class="auth-address">{email}</strong></p>
            <p class="auth-instructions"><AppIcon name="clock" /><span>Click the link to continue.<br />It works once and expires in 15 minutes.</span></p>
            <p class="auth-help">Nothing yet? Check your spam folder, or request another link in a minute.</p>
            {error && <p class="alert" role="alert">{error}</p>}
            <button class="secondary auth-resend" type="button" onClick={requestLink} disabled={busy || remaining > 0}>
              {busy ? 'Sending…' : remaining > 0 ? `Resend in ${remaining}s` : 'Resend sign-in link'}
            </button>
            <button class="link auth-change" type="button" onClick={useDifferentEmail} disabled={busy}>Use a different email</button>
            <p class="auth-status" role="status">{resent && !error ? 'Another sign-in link has been requested. Check your inbox.' : ''}</p>
          </>
        ) : (
          <>
            <p class="auth-intro" id="login-help">We’ll email you a secure link to create and manage your events.</p>
            {linkError && <p class="alert" role="alert">This link has expired or already been used. Enter your email to request a fresh one.</p>}
            <form onSubmit={(e) => { e.preventDefault(); void requestLink(); }} class="auth-form" noValidate>
              <FieldHeader htmlFor="login-email" label="Email address" error={invalidEmail ? error : null} errorId="login-email-error" />
              <input id="login-email" ref={emailInput} type="email" name="email" required maxLength={254}
                autoComplete="email" inputMode="email" placeholder="you@example.com" value={email}
                aria-invalid={invalidEmail ? 'true' : undefined} aria-describedby={invalidEmail ? 'login-email-error login-help' : error ? 'login-help login-error' : 'login-help'}
                onInput={(e) => { setEmail((e.target as HTMLInputElement).value); setError(null); setInvalidEmail(false); }} />
              {error && !invalidEmail && <p id="login-error" class="alert" role="alert">{error}</p>}
              <button type="submit" disabled={busy}>{busy ? 'Sending…' : 'Email me a sign-in link'}<AppIcon name="arrow" /></button>
            </form>
            <p class="auth-reassurance"><AppIcon name="key" />No password to remember.</p>
            <p class="auth-note">New here? The same link gets you started.</p>
          </>
        )}
      </section>
      <a class="auth-back" href={`${BASE_PATH}/`}><AppIcon name="arrow-left" />Back to home</a>
    </main>
  );
}
